import { coresPrioridade } from '@/components/gestao-bdgds-tarefas/StatusPrioridade.vue';
import { coresStatusErro } from '@/components/gestao-bdgds-tarefas/StatusErro.vue';

export const paletaDashboard = Object.freeze({
  volume: '#550899',
  lavanda: '#AC4DFF',
  lima: '#AAF766',
  verde: '#518E16',
  azul: '#2B60D6',
  aqua: '#00CDE2',
  violeta: '#8400F8',
  alerta: '#FF6A00',
  sienna: '#A34200',
  texto: '#1B1B1B',
  apoio: '#5A5A5A',
  neutro: '#BCBCBC',
  outras: '#8E8E8E',
  trilha: '#E7E4F1'
});

const verdeProgresso = '#6BCF2E';

const sequenciaNorven = [
  paletaDashboard.volume,
  paletaDashboard.lavanda,
  paletaDashboard.azul,
  paletaDashboard.aqua,
  paletaDashboard.verde,
  paletaDashboard.violeta,
  paletaDashboard.lima,
  paletaDashboard.sienna
];

const sequenciaOrigem = sequenciaNorven.map((cor) => (cor === paletaDashboard.azul ? verdeProgresso : cor));

const rotuloEixo = { color: paletaDashboard.apoio, fontSize: '12px', fontWeight: '400' };

const legenda = {
  itemStyle: { color: paletaDashboard.apoio, fontSize: '12px', fontWeight: '400' },
  symbolRadius: 2
};

const eixoY = {
  min: 0,
  title: { text: null },
  allowDecimals: false,
  gridLineColor: paletaDashboard.trilha,
  labels: { style: rotuloEixo }
};

const base = {
  credits: { enabled: false },
  title: { text: null },
  legend: { enabled: false },
  chart: {
    backgroundColor: 'transparent',
    style: { fontFamily: 'inherit' }
  }
};

const tooltipValor = {
  pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)'
};

const ordemStatus = [
  'IDENTIFICADO',
  'EM_DIAGNOSTICO',
  'EM_CORRECAO',
  'AGUARDANDO_VALIDACAO',
  'ENCERRADO',
  'REABERTO',
  'NAO_APLICAVEL'
];

function itens(dados) {
  return (dados && dados.itens) || [];
}

function ordenarStatus(lista) {
  return lista.slice().sort((atual, proximo) => {
    const indiceAtual = ordemStatus.indexOf(atual.chave);
    const indiceProximo = ordemStatus.indexOf(proximo.chave);

    return (indiceAtual === -1 ? ordemStatus.length : indiceAtual) - (indiceProximo === -1 ? ordemStatus.length : indiceProximo);
  });
}

export function opcoesBarrasResponsavel(dados) {
  const linhas = itens(dados);

  return {
    ...base,
    chart: { ...base.chart, type: 'bar', height: Math.max(220, linhas.length * 36 + 60) },
    xAxis: {
      categories: linhas.map((item) => item.rotulo),
      labels: {
        style: rotuloEixo,
        formatter() {
          const linha = linhas[this.pos];
          if (linha && linha.destaque) {
            return `<span style="color:${paletaDashboard.sienna};font-weight:600">${this.value}</span>`;
          }
          return this.value;
        }
      }
    },
    yAxis: eixoY,
    legend: { ...legenda, enabled: true },
    tooltip: { shared: true },
    plotOptions: { series: { stacking: 'normal', borderWidth: 0 } },
    series: (dados.series || []).map((serie) => ({
      name: serie.rotulo,
      color: coresStatusErro[serie.chave] || paletaDashboard.neutro,
      data: linhas.map((linha) => linha.valores[serie.chave] || 0)
    }))
  };
}

const roxoClaro = '#C5A4EA';

function misturarCor(origem, destino, fracao) {
  const canal = (inicio, fim) => Math.round(inicio + (fim - inicio) * fracao);
  const ler = (hex) => [1, 3, 5].map((posicao) => parseInt(hex.slice(posicao, posicao + 2), 16));
  const [vermelho, verde, azul] = ler(origem);
  const [vermelhoDestino, verdeDestino, azulDestino] = ler(destino);
  const hex = (valor) => valor.toString(16).padStart(2, '0');

  return `#${hex(canal(vermelho, vermelhoDestino))}${hex(canal(verde, verdeDestino))}${hex(canal(azul, azulDestino))}`;
}

function tomRoxoPorValor(valor, minimo, maximo) {
  if (maximo <= minimo) return paletaDashboard.volume;

  const fracaoClara = 1 - (valor - minimo) / (maximo - minimo);

  return misturarCor(paletaDashboard.volume, roxoClaro, fracaoClara);
}

function pontoTreemap(item, total, cor) {
  const ler = [1, 3, 5].map((posicao) => parseInt(cor.slice(posicao, posicao + 2), 16));
  const luminancia = (0.299 * ler[0] + 0.587 * ler[1] + 0.114 * ler[2]) / 255;

  return {
    name: item.rotulo,
    value: item.valor,
    percentual: total ? (item.valor / total) * 100 : 0,
    color: cor,
    dataLabels: {
      style: {
        color: luminancia < 0.55 ? '#FFFFFF' : paletaDashboard.texto,
        textOutline: 'none',
        fontWeight: '500'
      }
    }
  };
}

export function opcoesTreemap(dados) {
  const total = dados.total || 0;
  const entidades = itens(dados).filter((item) => item.rotulo !== 'Outras');
  const outras = itens(dados).filter((item) => item.rotulo === 'Outras');
  const valores = entidades.map((item) => item.valor);
  const maximo = Math.max(...valores);
  const minimo = Math.min(...valores);

  return {
    ...base,
    chart: { ...base.chart, height: 280 },
    tooltip: {
      headerFormat: '',
      pointFormat: '{point.name}<br><b>{point.value}</b> ({point.percentual:.1f}%)'
    },
    series: [
      {
        type: 'treemap',
        layoutAlgorithm: 'squarified',
        borderColor: '#ffffff',
        borderWidth: 2,
        dataLabels: { enabled: true, format: '{point.name}<br>{point.value}' },
        data: entidades
          .map((item) => pontoTreemap(item, total, tomRoxoPorValor(item.valor, minimo, maximo)))
          .concat(outras.map((item) => pontoTreemap(item, total, verdeProgresso)))
      }
    ]
  };
}

export function opcoesPareto(dados) {
  const referencia = dados.referencia_percentual || 80;

  return {
    ...base,
    chart: { ...base.chart, height: 300 },
    xAxis: { categories: itens(dados).map((item) => item.rotulo), labels: { style: rotuloEixo } },
    yAxis: [
      eixoY,
      {
        title: { text: null },
        min: 0,
        max: 100,
        opposite: true,
        labels: { format: '{value}%' },
        plotLines: [
          {
            value: referencia,
            color: paletaDashboard.sienna,
            dashStyle: 'Dash',
            width: 1,
            zIndex: 4,
            label: { text: `${referencia}%` }
          }
        ]
      }
    ],
    tooltip: { shared: true },
    series: [
      {
        type: 'column',
        name: 'Erros',
        data: itens(dados).map((item, indice) => ({
          y: item.valor,
          color: sequenciaNorven[Math.min(indice, sequenciaNorven.length - 1)]
        })),
        zIndex: 2
      },
      { type: 'pareto', name: '% acumulado', color: paletaDashboard.alerta, yAxis: 1, zIndex: 3, baseSeries: 0 }
    ]
  };
}

export function opcoesBarrasHorizontais(dados) {
  const linhas = itens(dados);

  return {
    ...base,
    chart: { ...base.chart, type: 'bar', height: Math.max(220, linhas.length * 28 + 40) },
    xAxis: { categories: linhas.map((item) => item.rotulo), reversed: true, labels: { style: rotuloEixo } },
    yAxis: eixoY,
    tooltip: { pointFormat: '<b>{point.y}</b>' },
    plotOptions: { bar: { borderWidth: 0, color: paletaDashboard.volume } },
    series: [{ name: 'Erros', data: linhas.map((item) => item.valor) }]
  };
}

export function opcoesAreaRecorrentes(dados) {
  const linhas = itens(dados);

  return {
    ...base,
    chart: { ...base.chart, type: 'areaspline', height: 180 },
    xAxis: { categories: linhas.map((item) => item.rotulo), labels: { style: rotuloEixo } },
    yAxis: eixoY,
    tooltip: { pointFormat: '<b>{point.y}</b>' },
    plotOptions: {
      areaspline: { fillColor: paletaDashboard.trilha, fillOpacity: 1, lineWidth: 2.5, marker: { enabled: false } }
    },
    series: [
      {
        name: 'Recorrentes',
        color: paletaDashboard.volume,
        data: linhas.map((item) => item.valor)
      }
    ]
  };
}

export function opcoesBarraOrigem(dados) {
  return {
    ...base,
    chart: { ...base.chart, type: 'bar', height: 140 },
    xAxis: { categories: ['Origem'], labels: { enabled: false } },
    yAxis: { min: 0, max: 100, visible: false },
    legend: { ...legenda, enabled: true },
    tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
    plotOptions: { series: { stacking: 'percent', borderWidth: 0, pointWidth: 40 } },
    series: itens(dados).map((item, indice) => ({
      name: `${item.rotulo} (${item.percentual}%)`,
      data: [item.valor],
      color:
        item.chave === 'NAO_INFORMADA'
          ? paletaDashboard.neutro
          : sequenciaOrigem[indice % sequenciaOrigem.length]
    }))
  };
}

function centralizarTotalRosca() {
  const serie = this.series && this.series[0];
  if (!serie || !serie.center || !this.title) return;

  const x = Math.round(this.plotLeft + serie.center[0]);
  const y = Math.round(this.plotTop + serie.center[1]);
  if (this.centroRoscaX === x && this.centroRoscaY === y) return;

  this.centroRoscaX = x;
  this.centroRoscaY = y;
  this.title.attr({ x, y: y - 6 });
  if (this.subtitle) this.subtitle.attr({ x, y: y + 12 });
}

export function opcoesRosca(dados) {
  return {
    ...base,
    chart: {
      ...base.chart,
      type: 'pie',
      height: 280,
      events: { render: centralizarTotalRosca }
    },
    title: {
      text: String(dados.total || 0),
      align: 'center',
      floating: true,
      verticalAlign: 'middle',
      style: { fontSize: '18px', fontWeight: '600', color: paletaDashboard.texto }
    },
    subtitle: {
      text: 'erros',
      align: 'center',
      floating: true,
      verticalAlign: 'middle',
      style: { fontSize: '11px', color: paletaDashboard.apoio }
    },
    legend: { ...legenda, align: 'right', enabled: true, layout: 'vertical', verticalAlign: 'middle' },
    tooltip: tooltipValor,
    plotOptions: {
      pie: {
        innerSize: '68%',
        borderWidth: 0,
        dataLabels: { enabled: false },
        showInLegend: true
      }
    },
    series: [
      {
        name: 'Erros',
        data: ordenarStatus(itens(dados)).map((item) => {
          const indice = ordemStatus.indexOf(item.chave);

          return {
            name: item.rotulo,
            y: item.valor,
            color: coresStatusErro[item.chave] || paletaDashboard.neutro,
            legendIndex: indice === -1 ? ordemStatus.length : indice
          };
        })
      }
    ]
  };
}

export function opcoesColunasPrioridade(dados) {
  return {
    ...base,
    chart: { ...base.chart, type: 'column', height: 280 },
    xAxis: { categories: itens(dados).map((item) => item.rotulo), labels: { style: rotuloEixo } },
    yAxis: eixoY,
    tooltip: { pointFormat: '<b>{point.y}</b>' },
    plotOptions: {
      column: {
        borderWidth: 0,
        colorByPoint: true,
        dataLabels: {
          enabled: true,
          style: { color: paletaDashboard.texto, fontSize: '12px', fontWeight: '600', textOutline: 'none' }
        }
      }
    },
    series: [
      {
        name: 'Erros',
        data: itens(dados).map((item) => ({
          y: item.valor,
          color: coresPrioridade[item.chave] || paletaDashboard.neutro
        }))
      }
    ]
  };
}

export function opcoesColunasRodada(dados) {
  const rodadas = itens(dados);

  return {
    ...base,
    chart: { ...base.chart, type: 'column', height: 280 },
    xAxis: {
      categories: rodadas.map((item) => item.rotulo),
      labels: {
        style: rotuloEixo,
        formatter() {
          const rodada = rodadas[this.pos];
          if (rodada && rodada.nome_teste) return `${this.value}<br>${rodada.nome_teste}`;

          return this.value;
        }
      }
    },
    yAxis: eixoY,
    tooltip: {
      headerFormat: '',
      pointFormatter() {
        const nome = this.nome_teste ? `${this.nome_teste}<br>` : '';

        return `${this.category}<br>${nome}<b>${this.y}</b>`;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          style: { color: paletaDashboard.apoio, fontSize: '11px', fontWeight: '500', textOutline: 'none' }
        }
      }
    },
    series: [
      {
        name: 'Erros novos',
        color: paletaDashboard.volume,
        data: rodadas.map((item) => ({
          y: item.valor,
          nome_teste: item.nome_teste
        }))
      }
    ]
  };
}
